import {cn} from '@features/ui/utils';
import {Slot} from '@radix-ui/react-slot';
import type {ButtonHTMLAttributes, DetailedHTMLProps} from 'react';

type ButtonVariant =
  | 'primary'
  | 'dark'
  | 'secondary'
  | 'tertiary'
  | 'primary-inactive'
  | 'ghost'
  | 'ghost-alt'
  | 'disabled'
  | 'filter'
  | 'filter-active';
type ButtonSize = 'xs' | 'sm' | 'md';

export const Button = ({
  children,
  asChild = false,
  variant = 'primary',
  size = 'md',
  className,
  keepStyleWhenDisabled = false,
  ...props
}: DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  keepStyleWhenDisabled?: boolean;
}) => {
  const Comp = asChild ? Slot : 'button';
  const isDisabled = variant === 'disabled' || props.disabled;
  const showDisabledStyle = isDisabled && !keepStyleWhenDisabled;

  return (
    <Comp
      className={cn(
        {
          'bg-primary text-white': variant === 'primary',
          'bg-gray-900 text-white hover:bg-gray-800': variant === 'dark',
          'bg-[#F1F1F1] text-[#8B8B8B]': variant === 'primary-inactive',
          'bg-secondary text-secondary-foreground': variant === 'secondary',
          'border-border/30 box-border border border-solid text-primary':
            variant === 'tertiary',
          'text-[#0B99FF]': variant === 'ghost',
          'bg-transparent text-[#FFF] hover:bg-white/10':
            variant === 'ghost-alt',
          'box-border inline-flex h-auto items-center justify-center self-start rounded-full border border-black/10 px-2 py-0.5 text-sm !font-medium !text-gray-400 hover:!text-gray-500':
            variant === 'filter',
          'box-border inline-flex h-auto items-center justify-center self-start rounded-full border border-transparent bg-secondary px-2 py-0.5 text-sm !font-medium !text-secondary-foreground':
            variant === 'filter-active',
          'h-10 rounded-lg px-4 py-3': size === 'md',
          'rounded-lg px-3 py-1.5': size === 'sm',
          'rounded-full px-2 py-0.5': size === 'xs',
          'cursor-not-allowed bg-[#A0A0A0]/10 text-black/50': showDisabledStyle,
          'cursor-not-allowed': isDisabled && keepStyleWhenDisabled,
        },
        `grid select-none content-center self-stretch text-sm font-semibold`,
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
};
